"use client";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import type { CAMonthPoint } from "@/hooks/use-dashboard-data";

// ── CA Mensuel Chart ──
export function CAChart({ data }: { data: CAMonthPoint[] }) {
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="mb-3 text-sm font-semibold text-muted-foreground">
          CA Mensuel
        </h3>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={data}>
            <XAxis
              dataKey="month"
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) =>
                v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)
              }
              width={45}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: 8,
                color: "hsl(var(--foreground))",
              }}
              labelStyle={{ color: "hsl(var(--muted-foreground))" }}
              formatter={(v) => [
                `${Number(v ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`,
                "CA",
              ]}
            />
            <Bar
              dataKey="ca"
              fill="#10B981"
              radius={[4, 4, 0, 0]}
              barSize={20}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ── Marge Gauge ──
export function MargeGauge({
  margePercent,
  margeEuros,
}: {
  margePercent: number;
  margeEuros: number;
}) {
  const R = 56;
  const C = 2 * Math.PI * R;
  const arcFraction = 0.75;
  const arcLen = C * arcFraction;
  const gapLen = C * (1 - arcFraction);
  const fillLen = arcLen * (Math.min(margePercent, 100) / 100);
  const offset = arcLen - fillLen;

  const color =
    margePercent >= 20
      ? "#10B981"
      : margePercent >= 10
        ? "#F59E0B"
        : "#EF4444";

  return (
    <Card>
      <CardContent className="flex flex-col items-center p-4">
        <h3 className="mb-3 text-sm font-semibold text-muted-foreground">
          Marge Annuelle
        </h3>
        <div className="relative h-36 w-36">
          <svg viewBox="0 0 140 140" className="-rotate-[135deg]">
            <circle
              cx="70"
              cy="70"
              r={R}
              fill="none"
              stroke="hsl(var(--muted))"
              strokeWidth="10"
              strokeDasharray={`${arcLen} ${gapLen}`}
              strokeLinecap="round"
            />
            <circle
              cx="70"
              cy="70"
              r={R}
              fill="none"
              stroke={color}
              strokeWidth="10"
              strokeDasharray={`${arcLen} ${gapLen}`}
              strokeDashoffset={offset}
              strokeLinecap="round"
              className="transition-all duration-1000"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold" style={{ color }}>
              {margePercent}%
            </span>
          </div>
        </div>
        <p className="mt-1 text-base font-semibold tabular-nums">
          {margeEuros.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
        </p>
      </CardContent>
    </Card>
  );
}

// ── Stat Counters (Acceptés / Facturés) ──
export function StatCounters({
  acceptes,
  factures,
}: {
  acceptes: number;
  factures: number;
}) {
  const maxBar = Math.max(acceptes, factures, 1);

  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="mb-4 text-sm font-semibold text-muted-foreground">
          Devis & Factures
        </h3>
        <div className="space-y-5">
          <div>
            <div className="mb-1.5 flex justify-between text-sm">
              <span className="text-muted-foreground">Acceptés</span>
              <span className="font-bold text-emerald-500">{acceptes}</span>
            </div>
            <Progress
              value={Math.min((acceptes / maxBar) * 100, 100)}
              className="h-2 [&>div]:bg-emerald-500"
            />
          </div>
          <div>
            <div className="mb-1.5 flex justify-between text-sm">
              <span className="text-muted-foreground">Facturés</span>
              <span className="font-bold text-blue-500">{factures}</span>
            </div>
            <Progress
              value={Math.min((factures / maxBar) * 100, 100)}
              className="h-2 [&>div]:bg-blue-500"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
